<script setup lang="ts">
    import { ref, reactive, watch } from 'vue';
    import { getAccounts } from './User.access';
    import List from '@shared/components/List.vue';
    import Loading from '@shared/components/Loading.vue';
    import BSFormSearch from '@shared/components/BSFormSearch.vue';
    import { formatDate } from '@cwd/Shared/utils/formatDateTime';
    import { AccountListItem } from './AccountReference';
    import { debounce } from '@shared/utils';
    import { UserAccount } from './BackOfficeUser';

    const props = defineProps<{
        userAccounts: UserAccount[];
    }>();

    const emit = defineEmits(['add-account']);

    const addAccountState = reactive<{
        accounts: AccountListItem[];
        loadingAccounts: boolean;
        filter: string;
    }>({
        accounts: [],
        loadingAccounts: false,
        filter: '',
    });

    watch(
        () => addAccountState.filter,
        (newVal, oldVal) => {
            if (oldVal === undefined) return;

            debounce(
                async () => {
                    await loadAccounts();
                },
                { delay: 300 }
            );
        },
        { immediate: true }
    );

    async function loadAccounts() {
        addAccountState.loadingAccounts = true;
        await getAccounts(addAccountState.filter)
            .then((response) => {
                const existingAccountIds = new Set(props.userAccounts.map((account) => account.AccountId));
                addAccountState.accounts = response.Results.filter((account) => !existingAccountIds.has(account.AccountId));
            })
            .finally(() => {
                addAccountState.loadingAccounts = false;
            });
    }

    async function addAccount(accountId: string) {
        emit('add-account', accountId);
        addAccountState.accounts = addAccountState.accounts.filter((account) => account.AccountId !== accountId);
    }
</script>

<template>
    <Loading :show="addAccountState.loadingAccounts" loadingStyle="progress-bar"></Loading>
    <div style="max-height: 600px; overflow-y: auto">
        <List
            displayAs="table"
            itemId="AccountId"
            :items="addAccountState.accounts"
            :tableHeaders="['#', 'Account', 'Client Company', 'Created', '']"
            :serverSideFilter="true"
            :hideSearch="true">
            <template #search>
                <BSFormSearch
                    v-model="addAccountState.filter"
                    inputId="add-account-list-search"
                    style="min-width: 400px"
                    :disabled="false"
                    @reset="() => (addAccountState.filter = '')"></BSFormSearch>
            </template>
            <template #item="{ item: account, index: index }">
                <td>{{ index + 1 }}.</td>
                <td>{{ account.Name }}</td>
                <td>{{ account.ClientCompany.Name }}</td>
                <td>{{ formatDate(account.Created) }}</td>
                <td class="autofit">
                    <button class="btn btn-primary btn-sm" @click="addAccount(account.AccountId)">Add</button>
                </td>
            </template>
        </List>
    </div>
</template>
